import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnerAdminContext = createContext();

// Role-based permissions mapping (matches backend ROLE_PERMISSIONS)
const ROLE_PERMISSIONS = {
  owner: {
    // Dashboard & Analytics
    view_dashboard: true,
    view_analytics: true,
    view_revenue_reports: true,
    view_system_health: true,
    // Customer Management
    view_customers: true,
    edit_customers: true,
    delete_customers: true,
    view_customer_details: true,
    add_internal_notes: true,
    // User Management
    view_users: true,
    search_users: true,
    impersonate_users: true,
    reset_user_passwords: true,
    // Billing & Subscriptions
    view_billing: true,
    change_plans: true,
    issue_refunds: true,
    apply_credits: true,
    cancel_subscriptions: true,
    // Support Tickets
    view_tickets: true,
    view_all_tickets: true,
    create_tickets: true,
    assign_tickets: true,
    close_tickets: true,
    delete_tickets: true,
    manage_templates: true,
    // Staff Management
    view_staff: true,
    manage_staff: true,
    invite_staff: true,
    edit_staff_roles: true,
    remove_staff: true,
    manage_permissions: true,
    // System Settings
    view_system_logs: true,
    view_audit_log: true,
    manage_feature_flags: true,
    manage_config: true,
    edit_settings: true,
  },
  super_admin: {
    view_dashboard: true,
    view_analytics: true,
    view_revenue_reports: true,
    view_system_health: true,
    view_customers: true,
    edit_customers: true,
    delete_customers: false,
    view_customer_details: true,
    add_internal_notes: true,
    view_users: true,
    search_users: true,
    impersonate_users: true,
    reset_user_passwords: true,
    view_billing: true,
    change_plans: true,
    issue_refunds: true,
    apply_credits: true,
    cancel_subscriptions: true,
    view_tickets: true,
    view_all_tickets: true,
    create_tickets: true,
    assign_tickets: true,
    close_tickets: true,
    delete_tickets: true,
    manage_templates: true,
    view_staff: true,
    manage_staff: true,
    invite_staff: true,
    edit_staff_roles: true,
    remove_staff: false,
    manage_permissions: false,
    view_system_logs: true,
    view_audit_log: true,
    manage_feature_flags: true,
    manage_config: true,
    edit_settings: false,
  },
  support_manager: {
    view_dashboard: true,
    view_analytics: false,
    view_revenue_reports: false,
    view_system_health: true,
    view_customers: true,
    edit_customers: false,
    delete_customers: false,
    view_customer_details: true,
    add_internal_notes: true,
    view_users: true,
    search_users: true,
    impersonate_users: false,
    reset_user_passwords: true,
    view_billing: true,
    change_plans: false,
    issue_refunds: false,
    apply_credits: false,
    cancel_subscriptions: false,
    view_tickets: true,
    view_all_tickets: true,
    create_tickets: true,
    assign_tickets: true,
    close_tickets: true,
    delete_tickets: false,
    manage_templates: true,
    view_staff: true,
    manage_staff: false,
    invite_staff: false,
    edit_staff_roles: false,
    remove_staff: false,
    manage_permissions: false,
    view_system_logs: false,
    view_audit_log: true,
    manage_feature_flags: false,
    edit_settings: false,
  },
  support_agent: {
    view_dashboard: true,
    view_analytics: false,
    view_revenue_reports: false,
    view_system_health: false,
    view_customers: true,
    edit_customers: false,
    delete_customers: false,
    view_customer_details: true,
    add_internal_notes: true,
    view_users: true,
    search_users: true,
    impersonate_users: false,
    reset_user_passwords: false,
    view_billing: false,
    change_plans: false,
    issue_refunds: false,
    apply_credits: false,
    cancel_subscriptions: false,
    view_tickets: true,
    view_all_tickets: false,
    view_assigned_tickets: true,
    create_tickets: true,
    assign_tickets: false,
    close_tickets: true,
    delete_tickets: false,
    manage_templates: false,
    view_staff: false,
    manage_staff: false,
    invite_staff: false,
    edit_staff_roles: false,
    remove_staff: false,
    manage_permissions: false,
    view_system_logs: false,
    view_audit_log: false,
    manage_feature_flags: false,
    edit_settings: false,
  },
  billing_specialist: {
    view_dashboard: true,
    view_analytics: true,
    view_revenue_reports: true,
    view_system_health: false,
    view_customers: true,
    edit_customers: false,
    delete_customers: false,
    view_customer_details: true,
    add_internal_notes: false,
    view_users: false,
    search_users: false,
    impersonate_users: false,
    reset_user_passwords: false,
    view_billing: true,
    change_plans: true,
    issue_refunds: true,
    apply_credits: true,
    cancel_subscriptions: true,
    view_tickets: true,
    view_billing_tickets: true,
    view_all_tickets: false,
    create_tickets: false,
    assign_tickets: false,
    close_tickets: false,
    delete_tickets: false,
    manage_templates: false,
    view_staff: false,
    manage_staff: false,
    invite_staff: false,
    edit_staff_roles: false,
    remove_staff: false,
    manage_permissions: false,
    view_system_logs: false,
    view_audit_log: true,
    manage_feature_flags: false,
    edit_settings: false,
  },
  developer: {
    view_dashboard: true,
    view_analytics: false,
    view_revenue_reports: false,
    view_system_health: true,
    view_customers: false,
    edit_customers: false,
    delete_customers: false,
    view_customer_details: false,
    add_internal_notes: false,
    view_users: false,
    search_users: false,
    impersonate_users: false,
    reset_user_passwords: false,
    view_billing: false,
    change_plans: false,
    issue_refunds: false,
    apply_credits: false,
    cancel_subscriptions: false,
    view_tickets: true,
    view_bug_reports: true,
    view_all_tickets: false,
    create_tickets: false,
    assign_tickets: false,
    close_tickets: false,
    delete_tickets: false,
    manage_templates: false,
    view_staff: false,
    manage_staff: false,
    invite_staff: false,
    edit_staff_roles: false,
    remove_staff: false,
    manage_permissions: false,
    view_system_logs: true,
    view_audit_log: true,
    manage_feature_flags: true,
    edit_settings: false,
  },
  auditor: {
    view_dashboard: true,
    view_analytics: true,
    view_revenue_reports: true,
    view_system_health: true,
    view_customers: true,
    edit_customers: false,
    delete_customers: false,
    view_customer_details: true,
    add_internal_notes: false,
    view_users: true,
    search_users: true,
    impersonate_users: false,
    reset_user_passwords: false,
    view_billing: true,
    change_plans: false,
    issue_refunds: false,
    apply_credits: false,
    cancel_subscriptions: false,
    view_tickets: true,
    view_all_tickets: true,
    create_tickets: false,
    assign_tickets: false,
    close_tickets: false,
    delete_tickets: false,
    manage_templates: false,
    view_staff: true,
    manage_staff: false,
    invite_staff: false,
    edit_staff_roles: false,
    remove_staff: false,
    manage_permissions: false,
    view_system_logs: true,
    view_audit_log: true,
    manage_feature_flags: false,
    edit_settings: false,
  },
};

export function OwnerAdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewAsRole, setViewAsRole] = useState(null); // "View As" role override

  // Check if admin is authenticated
  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/auth/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdmin(data);
      } else {
        setAdmin(null);
      }
    } catch (err) {
      console.error('Admin auth check failed:', err);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      if (data.requires_mfa) {
        return { requiresMfa: true, mfaToken: data.mfa_session_token };
      }
      
      setAdmin(data.admin);
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Verify MFA
  const verifyMfa = async (mfaToken, code) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/owner-admin/auth/mfa/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_session_token: mfaToken, code })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'MFA verification failed');
      }
      
      setAdmin(data.admin);
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/owner-admin/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setAdmin(null);
    }
  };

  // Get permissions for current admin's role (or viewAs role if active)
  const rolePermissions = useMemo(() => {
    // If viewing as another role, use that role's permissions
    if (viewAsRole) {
      return ROLE_PERMISSIONS[viewAsRole] || {};
    }
    if (!admin?.role) return {};
    return ROLE_PERMISSIONS[admin.role] || {};
  }, [admin?.role, viewAsRole]);

  // Check permission - supports both role-based and list-based permissions
  const hasPermission = useCallback((permission) => {
    if (!admin) return false;
    
    // If viewing as another role, only use that role's permissions
    if (viewAsRole) {
      return ROLE_PERMISSIONS[viewAsRole]?.[permission] === true;
    }
    
    // If permissions is a list (e.g., ['*']), check for wildcard or specific permission
    if (Array.isArray(admin.permissions)) {
      if (admin.permissions.includes('*')) return true;
      if (admin.permissions.includes(permission)) return true;
    }
    
    // Check role-based permissions
    return rolePermissions[permission] === true;
  }, [admin, rolePermissions, viewAsRole]);

  // Get all permissions for current role (for UI display)
  const getAllPermissions = useCallback(() => {
    return rolePermissions;
  }, [rolePermissions]);

  // Check if current user can use "View As" feature (only owner and super_admin)
  const canViewAs = useMemo(() => {
    return admin?.role === 'owner' || admin?.role === 'super_admin';
  }, [admin?.role]);

  // Start viewing as another role
  const startViewAs = useCallback((role) => {
    if (!canViewAs) return;
    if (role === admin?.role) {
      setViewAsRole(null); // If selecting own role, exit view as mode
    } else {
      setViewAsRole(role);
    }
  }, [canViewAs, admin?.role]);

  // Exit "View As" mode
  const exitViewAs = useCallback(() => {
    setViewAsRole(null);
  }, []);

  // Get the effective role (viewAs or actual)
  const effectiveRole = useMemo(() => {
    return viewAsRole || admin?.role;
  }, [viewAsRole, admin?.role]);

  const value = {
    admin,
    loading,
    error,
    login,
    verifyMfa,
    logout,
    checkAuth,
    hasPermission,
    getAllPermissions,
    rolePermissions,
    isAuthenticated: !!admin,
    // View As feature
    viewAsRole,
    canViewAs,
    startViewAs,
    exitViewAs,
    effectiveRole,
    allRoles: Object.keys(ROLE_PERMISSIONS),
  };

  return (
    <OwnerAdminContext.Provider value={value}>
      {children}
    </OwnerAdminContext.Provider>
  );
}

export function useOwnerAdmin() {
  const context = useContext(OwnerAdminContext);
  if (!context) {
    throw new Error('useOwnerAdmin must be used within OwnerAdminProvider');
  }
  return context;
}

// Export role permissions for use in other components
export { ROLE_PERMISSIONS };
